import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import ArticleCard from '@/components/base/ArticleCard';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory } from '@/mocks/articles';
import useSEOMeta from '@/hooks/useSEOMeta';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) setActiveSlug(slug);
  }, [slug]);

  const activeCategory = categories.find((c) => c.slug === activeSlug) || categories[0];
  const categoryArticles = getArticlesByCategory(activeCategory.id);

  useSEOMeta({
    title: `${activeCategory.name} - 苍井空${activeCategory.description} | 苍井空人物志`,
    description: `苍井空${activeCategory.name}相关文章合集。${activeCategory.description}。共${categoryArticles.length}篇深度文章。`,
    keywords: `苍井空,${activeCategory.name},苍井空个人资料,苍井空转型经历`,
    canonicalUrl: `https://cangjingkong.cn/category/${activeSlug}`,
  });

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${activeCategory.name} - 苍井空人物志`,
        description: `苍井空${activeCategory.name}相关文章合集`,
        url: `https://cangjingkong.cn/category/${activeSlug}`,
        isPartOf: { '@type': 'WebSite', name: '苍井空人物志', url: 'https://cangjingkong.cn/' },
      })}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-8">
            <Breadcrumb
              items={[
                { name: '首页', path: '/' },
                { name: activeCategory.name },
              ]}
            />
          </div>

          <section className="pb-8">
            <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
              <div className="mb-8">
                <h1 className="text-2xl md:text-3xl font-semibold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  {activeCategory.name}
                </h1>
                <p className="mt-2 text-foreground-600">
                  {activeCategory.description}
                </p>
              </div>

              <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    onClick={() => {
                      window.scrollTo({ top: 0, behavior: 'smooth' });
                    }}
                    className={`px-4 py-2 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-background-50 font-medium'
                        : 'bg-background-100 text-foreground-700 hover:bg-background-200/70'
                    }`}
                  >
                    {cat.name}
                    <span className="ml-1.5 text-xs opacity-70">({cat.articleCount})</span>
                  </Link>
                ))}
              </div>

              {categoryArticles.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                  {categoryArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                    <i className="ri-article-line text-2xl"></i>
                  </div>
                  <p className="mt-4 text-foreground-500">该分类暂无文章</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}